import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getResults, getPools } from '../services/api';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { useDashboard } from '../context/DashboardContext';
import Select from 'react-select';
import { FaTimes, FaArrowRight, FaRegCalendarAlt } from 'react-icons/fa';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { format } from 'date-fns';

const ProfileOverlay = ({ player, onClose }) => {
    if (!player) return null;

    // Helper function to format location
    const formatLocation = (location) => {
        if (!location) return 'Location not set';
        if (typeof location === 'string') return location;

        const parts = [];
        if (location.sublocality) parts.push(location.sublocality);
        if (location.locality) parts.push(location.locality);
        if (location.administrative_area_level_1) parts.push(location.administrative_area_level_1);
        if (location.country) parts.push(location.country);
        // Exclude administrative_area_level_2 and any empty/null fields

        return parts.length > 0 ? parts.join(', ') : 'Location not set';
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 relative">
                {/* Close button */}
                <button 
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                >
                    ✕
                </button>

                {/* Profile Content */}
                <div className="flex flex-col items-center">
                    {player.picture ? (
                        <div className="w-32 h-32 rounded-full overflow-hidden mb-4">
                            <img 
                                src={player.picture} 
                                alt={player.name} 
                                className="h-full w-full object-cover"
                            />
                        </div>
                    ) : (
                        <div className="w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center mb-4">
                            <span className="text-4xl text-gray-500">{player.name[0]}</span>
                        </div>
                    )}
                    <h3 className="text-xl font-semibold mb-2">{player.name}</h3>
                    <div className="text-gray-600 text-center">
                        <p className="mb-2">Age: {player.age || 'N/A'}</p>
                        <p className="mb-2">Gender: {player.gender || 'N/A'}</p>
                        <p className="mb-2">Location: {formatLocation(player.location)}</p>
                        <div className="mt-4 grid grid-cols-2 gap-4 text-center">
                            <div>
                                <p className="font-semibold">Singles Points</p>
                                <p>{player.singlesPoints || 0}</p>
                            </div>
                            <div>
                                <p className="font-semibold">Doubles Points</p>
                                <p>{player.doublesPoints || 0}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Helper function to format pool location string (format: "country#admin1#admin2#locality#sublocality")
const formatPoolLocationString = (location) => {
    // Handle both string and object formats
    let locationString = '';
    if (typeof location === 'string') {
        locationString = location;
    } else if (location && typeof location === 'object') {
        // If it's an object, format it similar to user locations
        const parts = [];
        if (location.sublocality) parts.push(location.sublocality);
        if (location.locality) parts.push(location.locality);
        if (location.administrative_area_level_1) parts.push(location.administrative_area_level_1);
        if (location.country) parts.push(location.country);
        return parts.length > 0 ? parts.join(', ') : '';
    }
    
    if (!locationString) return '';
    
    const parts = locationString.split('#').filter(Boolean);
    if (parts.length === 0) return '';
    
    // Format similar to user location: sublocality, locality, admin1, country (omitting admin2)
    const [country, admin1, admin2, locality, sublocality] = parts;
    const displayParts = [];
    if (sublocality) displayParts.push(sublocality);
    if (locality) displayParts.push(locality);
    if (admin1) displayParts.push(admin1);
    if (country) displayParts.push(country);
    
    return displayParts.join(', ');
};

// Pool Selection Modal Component
const PoolSelectionModal = ({ isOpen, onClose, pools, onSelect }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold">Select Pool</h2>
                    <button 
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        <FaTimes />
                    </button>
                </div>
                
                <div className="mb-4">
                    <Select
                        options={pools.map(pool => ({
                            value: pool.name,
                            label: (
                                <div className="flex items-center">
                                    {pool.picture ? (
                                        <img 
                                            src={pool.picture} 
                                            alt="" 
                                            className="w-8 h-8 rounded-full mr-2 object-cover"
                                        />
                                    ) : (
                                        <div className="w-8 h-8 rounded-full bg-gray-200 mr-2 flex items-center justify-center">
                                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                            </svg>
                                        </div>
                                    )}
                                    <div className="flex flex-col">
                                        <span className="font-medium">{pool.name}</span>
                                        {pool.location && (
                                            <span className="text-sm text-gray-500">
                                                {formatPoolLocationString(pool.location)}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )
                        }))}
                        onChange={(selected) => {
                            onSelect(selected.value);
                            onClose();
                        }}
                        placeholder="Search pools..."
                        className="w-full"
                        classNamePrefix="select"
                        isClearable
                    />
                </div>
            </div>
        </div>
    );
};

const Results = () => {
    const [players, setPlayers] = useState([]);
    const [filteredPlayers, setFilteredPlayers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('singles');
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const { isCacheValid } = useDashboard();

    // Filter states
    const [genderFilter, setGenderFilter] = useState('');
    const [poolFilter, setPoolFilter] = useState('');
    const [ageFilter, setAgeFilter] = useState('');
    const [pools, setPools] = useState([]);
    const [showPoolModal, setShowPoolModal] = useState(false);

    // Location filter states
    const [locations, setLocations] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState('');
    const [selectedRegion, setSelectedRegion] = useState('');
    const [selectedSubRegion, setSelectedSubRegion] = useState('');
    const [selectedLocality, setSelectedLocality] = useState('');

    // Date range picker state
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [dateRange, setDateRange] = useState([
      {
        startDate: null,
        endDate: null,
        key: 'selection'
      }
    ]);
    const [appliedDateRange, setAppliedDateRange] = useState(null);

    /**
     * activeLocation initialization priority:
     * 1. Use cache for results if available
     * 2. Use user's location from profile
     * 3. Fallback to empty string
     */
    const [activeLocation, setActiveLocation] = useState(() => {
        const cache = getCache('results');
        const cacheLoc = cache.data?.locationString;
        if (cacheLoc) return cacheLoc;
        const storedProfile = JSON.parse(localStorage.getItem('userProfile'))?.data;
        return `${storedProfile?.location?.country}#${storedProfile?.location?.administrative_area_level_1}`;
    });

    useEffect(() => {
        // Load locations from cache
        const cachedLocations = JSON.parse(localStorage.getItem('locations'))?.data;
        if (cachedLocations) {
            setLocations(cachedLocations);
        }
        
        // Initialize location dropdowns from cache or user profile
        const cache = getCache('results');
        const cacheLoc = cache.data?.locationString;
        

        
        if (cacheLoc) {
            // Restore from cache
            if (cacheLoc === '__GLOBAL__') {
                setSelectedCountry('__GLOBAL__');
            } else {
                const [country, region, subRegion, locality] = cacheLoc.split('#');
                setSelectedCountry(country || '');
                setSelectedRegion(region || '');
                setSelectedSubRegion(subRegion || '');
                setSelectedLocality(locality || '');
            }
            
            // Restore date range from cache if present
            if (cache.data?.startTimestamp && cache.data?.endTimestamp) {
                setAppliedDateRange({
                    startDate: new Date(cache.data.startTimestamp),
                    endDate: new Date(cache.data.endTimestamp)
                });
                setDateRange([{ 
                    startDate: new Date(cache.data.startTimestamp), 
                    endDate: new Date(cache.data.endTimestamp), 
                    key: 'selection' 
                }]);
            }
        } else {
            // Initialize from user profile
            const storedProfile = JSON.parse(localStorage.getItem('userProfile'))?.data;
            if (storedProfile?.location) {
                setSelectedCountry(storedProfile.location.country || '');
                setSelectedRegion(storedProfile.location.administrative_area_level_1 || '');
            }
        }
        
        // Load pools
        getPools().then(setPools).catch(console.error);
        
        // Fetch initial results with restored date range if any
        const restoredDateRange = (cache.data?.startTimestamp && cache.data?.endTimestamp) ? {
            startDate: new Date(cache.data.startTimestamp),
            endDate: new Date(cache.data.endTimestamp)
        } : null;
        fetchResults(null, restoredDateRange);
    }, []);

    // Define applyFilters function before using it
    const applyFilters = useCallback(() => {
        let newFilteredPlayers = [...players];

        // Filter by gender
        if (genderFilter !== '') {
            newFilteredPlayers = newFilteredPlayers.filter(player => player.gender === genderFilter);
        }

        // Filter by pool
        if (poolFilter) {
            newFilteredPlayers = newFilteredPlayers.filter(player => {
                // Check if the player is in the selected pool
                const selectedPoolObj = pools.find(pool => pool.name === poolFilter);
                return selectedPoolObj && selectedPoolObj.players && selectedPoolObj.players.includes(player.userId);
            });
        }

        // Filter by age group
        if (ageFilter) {
            newFilteredPlayers = newFilteredPlayers.filter(player => {
                const age = player.age;
                switch (ageFilter) {
                    case '<18':
                        return age < 18;
                    case '18-25':
                        return age >= 18 && age <= 25;
                    case '25-35':
                        return age > 25 && age <= 35;
                    case '35-45':
                        return age > 35 && age <= 45;
                    case '45+':
                        return age > 45;
                    default:
                        return true;
                }
            });
        }

        // Update the filtered players state
        setFilteredPlayers(newFilteredPlayers);
    }, [players, genderFilter, poolFilter, ageFilter, pools]);

    // Apply filters whenever players, filters, or activeTab changes
    useEffect(() => {
        applyFilters();
    }, [applyFilters, activeTab]);

    // No useEffect needed - API calls only on initial load and green button clicks

    // Helper to get cache object from localStorage
    function getCache(key) {
        const raw = localStorage.getItem(key);
        if (!raw) return { data: null, lastFetched: null };
        try {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === 'object' && 'data' in parsed && 'lastFetched' in parsed) {
                return parsed;
            } else {
                // Backward compatibility: treat as data only
                return { data: parsed, lastFetched: null };
            }
        } catch {
            return { data: null, lastFetched: null };
        }
    }

    const fetchResults = async (locationOverride = null, dateRangeOverride = null) => {
        try {
            const locationToUse = locationOverride || activeLocation;
            const dateRangeToUse = dateRangeOverride || appliedDateRange;
            
            // Prepare timestamps if date range is set
            let startTimestamp = null, endTimestamp = null;
            if (dateRangeToUse && dateRangeToUse.startDate && dateRangeToUse.endDate) {
                startTimestamp = new Date(dateRangeToUse.startDate).setHours(0,0,0,0);
                endTimestamp = new Date(dateRangeToUse.endDate).setHours(23,59,59,999);
            }
            
            // Check if cached results exist and are valid for this location/date combo
            const cache = getCache('results');
            const cacheLocationMatches = cache.data?.locationString === locationToUse;
            const cacheDateMatches = (!startTimestamp && !cache.data?.startTimestamp) || 
                (cache.data?.startTimestamp === startTimestamp && cache.data?.endTimestamp === endTimestamp);
            
            if (isCacheValid(cache.lastFetched) && cache.data && cacheLocationMatches && cacheDateMatches) {
                console.log('Using cached results');
                setPlayers(cache.data.results || cache.data);
                setFilteredPlayers(cache.data.results || cache.data);
                setLoading(false);
                return;
            }

            console.log('Fetching fresh results from API', { location: locationToUse, startTimestamp, endTimestamp });
            const data = await getResults(locationToUse, startTimestamp, endTimestamp);
            setPlayers(data);
            setFilteredPlayers(data);
            
            // Cache the results with metadata
            const cacheData = {
                results: data,
                locationString: locationToUse,
                startTimestamp,
                endTimestamp
            };
            localStorage.setItem('results', JSON.stringify({ data: cacheData, lastFetched: Date.now() }));
            
            setLoading(false);
        } catch (err) {
            console.error('Error fetching results:', err);
            setError('Failed to load results');
        } finally {
            setLoading(false);
        }
    };

    const clearFilter = (filterType) => {
        switch (filterType) {
            case 'gender':
                setGenderFilter('');
                break;
            case 'pool':
                setPoolFilter('');
                break;
            case 'age':
                setAgeFilter('');
                break;
            default:
                break;
        }
    };

    const handleLocationSearch = async () => {
        let loc;
        
        // Handle Global selection
        if (selectedCountry === '__GLOBAL__') {
            loc = '__GLOBAL__';
        } else {
            // Handle single location selection
            loc = selectedCountry;
            if (selectedRegion) loc += `#${selectedRegion}`;
            if (selectedSubRegion || selectedLocality) loc += `#${selectedSubRegion}`;
            if (selectedLocality) loc += `#${selectedLocality}`;
        }
        
        console.log('Location search:', loc);
        
        // Clear cache and fetch results directly with current location and date range
        localStorage.removeItem('results');
        setActiveLocation(loc);
        setLoading(true);
        
        try {
            await fetchResults(loc, appliedDateRange);
        } catch (err) {
            console.error('Error in handleLocationSearch:', err);
            setError(err.message);
        }
    };

    const getSortedPlayers = () => {
        return [...filteredPlayers]
            .filter(player => {
                // Filter out players with 0 wins and 0 losses for the active tab
                const wins = activeTab === 'singles' ? player.singlesWin : player.doublesWin;
                const losses = activeTab === 'singles' ? player.singlesLoss : player.doublesLoss;
                return wins > 0 || losses > 0; // Only include players who have played matches
            })
            .sort((a, b) => {
                const changeA = activeTab === 'singles' 
                    ? ((a.singlesEndPoints || a.singlesPoints) - a.singlesStartPoints)
                    : ((a.doublesEndPoints || a.doublesPoints) - a.doublesStartPoints);
                const changeB = activeTab === 'singles'
                    ? ((b.singlesEndPoints || b.singlesPoints) - b.singlesStartPoints)
                    : ((b.doublesEndPoints || b.doublesPoints) - b.doublesStartPoints);
                return changeB - changeA;
            });
    };

    // Get logged-in user profile and userId
    const userProfile = JSON.parse(localStorage.getItem('userProfile'))?.data;
    const userId = userProfile?.userId;

    // Helper to always use latest profile for logged-in user
    const getPlayerData = (player) => {
        if (!player) return null;
        if (player.userId === userId) {
            return {
                ...player,
                name: userProfile?.name || player.name,
                picture: userProfile?.picture || player.picture,
                gender: userProfile?.gender || player.gender,
                dob: userProfile?.dob || player.dob,
                location: userProfile?.location || player.location,
                age: userProfile?.age || player.age
            };
        }
        return player;
    };

    // Remove the full-page loading spinner - we'll show it in the table instead

    if (error) {
        return (
            <div className="p-6 text-red-500 text-center">
                {error}
            </div>
        );
    }

    return (
        <div className="p-1 sm:p-6">
            {/* Location dropdowns and date picker */}
            <div className="mb-4 flex items-center space-x-1 overflow-x-auto overflow-y-hidden">
                <select value={selectedCountry} onChange={(e) => {
                    setSelectedCountry(e.target.value);
                    setSelectedRegion('');
                    setSelectedSubRegion('');
                    setSelectedLocality('');
                }} className={`bg-white p-1 border rounded pr-4 appearance-none text-sm sm:text-base min-w-[60px] ${selectedCountry ? 'text-black' : 'text-gray-400'}`}>
                    <option value="__GLOBAL__">Global</option>
                    {locations.map(country => <option key={country.name} value={country.name}>{country.name}</option>)}
                </select>

                <select value={selectedRegion} onChange={(e) => {
                    setSelectedRegion(e.target.value);
                    setSelectedSubRegion('');
                    setSelectedLocality('');
                }} className={`bg-white p-1 border rounded pr-4 appearance-none text-sm sm:text-base min-w-[60px] ${selectedRegion ? 'text-black' : 'text-gray-400'}`} disabled={!selectedCountry || selectedCountry === '__GLOBAL__'}>
                    <option value="">Region</option>
                    {locations.find(c => c.name === selectedCountry)?.regions.map(region => <option key={region.name} value={region.name}>{region.name}</option>)}
                </select>

                {(() => {
                    const regionObj = locations.find(c => c.name === selectedCountry)?.regions.find(r => r.name === selectedRegion);
                    if (regionObj && regionObj.subRegions && regionObj.subRegions.length > 0) {
                        return (
                            <select value={selectedSubRegion} onChange={(e) => {
                                setSelectedSubRegion(e.target.value)
                                setSelectedLocality('');
                            }} className={`bg-white p-1 border rounded pr-4 appearance-none text-sm sm:text-base min-w-[60px] ${selectedSubRegion ? 'text-black' : 'text-gray-400'}`} disabled={!selectedRegion}>
                                <option value="">Sub-region</option>
                                {regionObj.subRegions.map(subRegion => <option key={subRegion.name} value={subRegion.name}>{subRegion.name}</option>)}
                            </select>
                        );
                    }
                    return null;
                })()}

                {(() => {
                    const regionObj = locations.find(c => c.name === selectedCountry)?.regions.find(r => r.name === selectedRegion);
                    if (!regionObj) return null;
                    let localities = [];
                    if (regionObj.subRegions && regionObj.subRegions.length > 0 && selectedSubRegion) {
                        const subRegionObj = regionObj.subRegions.find(sr => sr.name === selectedSubRegion);
                        localities = subRegionObj ? subRegionObj.localities : [];
                    } else if (!regionObj.subRegions || regionObj.subRegions.length === 0) {
                        localities = regionObj.localities || [];
                    }
                    return (
                        <select value={selectedLocality} onChange={(e) => setSelectedLocality(e.target.value)} className={`bg-white p-1 border rounded pr-4 appearance-none text-sm sm:text-base min-w-[60px] ${selectedLocality ? 'text-black' : 'text-gray-400'}`} disabled={localities.length === 0}>
                            <option value="">Locality</option>
                            {localities.map(locality => <option key={locality.name} value={locality.name}>{locality.name}</option>)}
                        </select>
                    );
                })()}

                <button
                  type="button"
                  className="bg-white p-1 border rounded appearance-none text-sm sm:text-base flex items-center justify-center"
                  style={{ height: '32px' }}
                  onClick={() => setShowDatePicker(true)}
                  title="Select date range"
                >
                  <FaRegCalendarAlt className="text-xl text-gray-600" />
                </button>

                <button className="p-2 bg-lime-400 text-white rounded hover:bg-lime-500" onClick={handleLocationSearch}>
                    <FaArrowRight />
                </button>
            </div>

            {/* Applied date range display */}
            {appliedDateRange && appliedDateRange.startDate && appliedDateRange.endDate && (
              <div className="mt-2 flex flex-wrap gap-2">
                <div className="relative inline-flex items-center bg-white border border-gray-200 rounded px-3 py-1 text-sm text-black font-normal pr-7">
                  <FaRegCalendarAlt className="mr-1 text-gray-500" />
                  <span>
                    {format(appliedDateRange.startDate, 'd MMM yy')} to {format(appliedDateRange.endDate, 'd MMM yy')}
                  </span>
                  <button
                    className="absolute top-1/2 right-2 transform -translate-y-1/2 text-red-500 hover:text-red-700 text-lg font-bold"
                    style={{ padding: 0, background: 'none', border: 'none', lineHeight: 1 }}
                    onClick={() => {
                      setAppliedDateRange(null);
                      setDateRange([{ startDate: null, endDate: null, key: 'selection' }]);
                    }}
                    title="Clear"
                  >
                    ×
                  </button>
                </div>
              </div>
            )}

            {/* Date picker modal */}
            {showDatePicker && (
              <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-4 shadow-lg">
                  <DateRange
                    editableDateInputs={true}
                    onChange={item => setDateRange([item.selection])}
                    moveRangeOnFirstSelection={false}
                    ranges={dateRange}
                    maxDate={new Date()}
                    showDateDisplay={false}
                    inputRanges={[]}
                    staticRanges={[]}
                  />
                  <div className="flex justify-end mt-2 gap-2">
                    <button
                      className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                      onClick={() => setShowDatePicker(false)}
                    >
                      Cancel
                    </button>
                    <button
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                      onClick={() => {
                        setAppliedDateRange(dateRange[0]);
                        setShowDatePicker(false);
                      }}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Tabs */}
            <div className="flex border-b border-gray-200 mb-6">
                <button
                    className={`px-4 sm:px-8 py-2 font-medium text-sm focus:outline-none ${
                        activeTab === 'singles'
                            ? 'border-b-2 border-black text-black'
                            : 'text-gray-500 hover:text-black'
                    }`}
                    onClick={() => setActiveTab('singles')}
                >
                    SINGLES
                </button>
                <button
                    className={`px-4 sm:px-8 py-2 font-medium text-sm focus:outline-none ${
                        activeTab === 'doubles'
                            ? 'border-b-2 border-black text-black'
                            : 'text-gray-500 hover:text-black'
                    }`}
                    onClick={() => setActiveTab('doubles')}
                >
                    DOUBLES
                </button>
            </div>

            {/* Filter dropdowns */}
            <div className="mb-4 flex items-center space-x-1 overflow-x-auto overflow-y-hidden">
                <div className="relative">
                    <select
                        value={genderFilter}
                        onChange={(e) => setGenderFilter(e.target.value)}
                        className={`bg-white p-1 border rounded pr-4 appearance-none text-sm sm:text-base min-w-[60px] ${genderFilter === '' ? 'text-gray-400' : 'text-black'}`}
                    >
                        <option value="" disabled hidden>Gender</option>
                        <option value="Male">Men</option>
                        <option value="Female">Ladies</option>
                    </select>
                    {genderFilter !== '' && (
                        <button
                            onClick={() => clearFilter('gender')}
                            className="absolute top-1/2 right-2 transform -translate-y-1/2 translate-x-1 text-red-500 hover:text-red-700 text-lg font-bold"
                            title="Clear"
                        >
                            ×
                        </button>
                    )}
                </div>

                <div className="relative">
                    <button
                        onClick={() => setShowPoolModal(true)}
                        className={`bg-white p-1 border rounded text-left pr-4 appearance-none text-sm sm:text-base min-w-[60px] ${
                            poolFilter ? 'text-black' : 'text-gray-400'
                        }`}
                        style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                        {poolFilter || 'Pool'}
                    </button>
                    {poolFilter && (
                        <button
                            onClick={() => clearFilter('pool')}
                            className="absolute top-1/2 right-2 transform -translate-y-1/2 translate-x-1 text-red-500 hover:text-red-700 text-lg font-bold"
                            title="Clear"
                        >
                            ×
                        </button>
                    )}
                </div>

                <div className="relative">
                    <select
                        value={ageFilter}
                        onChange={(e) => setAgeFilter(e.target.value)}
                        className={`bg-white p-1 border rounded pr-4 appearance-none text-sm sm:text-base min-w-[60px] ${ageFilter === '' ? 'text-gray-400' : 'text-black'}`}
                    >
                        <option value="" disabled hidden>Age group</option>
                        <option value="<18">&lt; 18</option>
                        <option value="18-25">18 - 25</option>
                        <option value="25-35">25 - 35</option>
                        <option value="35-45">35 - 45</option>
                        <option value="45+">45+</option>
                    </select>
                    {ageFilter && (
                        <button
                            onClick={() => clearFilter('age')}
                            className="absolute top-1/2 right-2 transform -translate-y-1/2 translate-x-1 text-red-500 hover:text-red-700 text-lg font-bold"
                            title="Clear"
                        >
                            ×
                        </button>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-1 sm:p-2">
                {/* Results Table */}
                <div className="overflow-x-auto">
                    <table className="min-w-full bg-white">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="py-1 px-1 sm:py-2 sm:px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                                <th className="py-1 px-1 sm:py-2 sm:px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24 sm:w-auto">Player</th>
                                <th className="py-1 px-1 sm:py-2 sm:px-6 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Win</th>
                                <th className="py-1 px-1 sm:py-2 sm:px-6 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Loss</th>
                                <th className="py-1 px-1 sm:py-2 sm:px-6 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Start</th>
                                <th className="py-1 px-1 sm:py-2 sm:px-6 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">End</th>
                                <th className="py-1 px-1 sm:py-2 sm:px-6 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Change</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="7" className="text-center py-4">
                                        <div className="flex justify-center items-center">
                                            <LoadingSpinner />
                                        </div>
                                    </td>
                                </tr>
                            ) : Array.isArray(getSortedPlayers()) && getSortedPlayers().length > 0 ? (
                                getSortedPlayers().map((player, index) => {
                                    const displayPlayer = getPlayerData(player);
                                    // Use end points for results table display (fallback to regular points for profile-based results)
                                    const endPoints = activeTab === 'singles' 
                                        ? (displayPlayer.singlesEndPoints || displayPlayer.singlesPoints)
                                        : (displayPlayer.doublesEndPoints || displayPlayer.doublesPoints);
                                    const startPoints = activeTab === 'singles' ? displayPlayer.singlesStartPoints : displayPlayer.doublesStartPoints;
                                    const wins = activeTab === 'singles' ? displayPlayer.singlesWin : displayPlayer.doublesWin;
                                    const losses = activeTab === 'singles' ? displayPlayer.singlesLoss : displayPlayer.doublesLoss;
                                    const change = endPoints - startPoints;

                                    if (!displayPlayer || !displayPlayer.name) {
                                        console.warn('Player is missing expected properties:', displayPlayer);
                                        return null;
                                    }

                                    return (
                                        <tr key={displayPlayer.userId}>
                                            <td className="py-1 px-1 sm:py-2 sm:px-6 whitespace-nowrap text-xs sm:text-sm text-gray-500">{index + 1}</td>
                                            <td className="py-1 px-1 sm:py-2 sm:px-6 whitespace-nowrap w-24 sm:w-auto">
                                                <div className="flex items-center cursor-pointer" onClick={() => setSelectedPlayer(displayPlayer)}>
                                                    {displayPlayer.picture ? (
                                                        <div className="h-6 w-6 sm:h-8 sm:w-8 rounded-full overflow-hidden flex-shrink-0 mr-1 sm:mr-3">
                                                            <img 
                                                                src={displayPlayer.picture} 
                                                                alt={displayPlayer.name} 
                                                                className="h-full w-full object-cover"
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div className="h-6 w-6 sm:h-8 sm:w-8 rounded-full bg-gray-200 mr-1 sm:mr-3 flex-shrink-0 flex items-center justify-center">
                                                            <span className="text-xs sm:text-sm text-gray-500">{displayPlayer.name[0]}</span>
                                                        </div>
                                                    )}
                                                    <div className="text-xs sm:text-sm font-medium text-gray-900 truncate max-w-24 sm:max-w-none" title={displayPlayer.name}>{displayPlayer.name}</div>
                                                </div>
                                            </td>
                                            <td className="py-1 px-1 sm:py-2 sm:px-6 whitespace-nowrap text-xs sm:text-sm text-center text-gray-500">{wins}</td>
                                            <td className="py-1 px-1 sm:py-2 sm:px-6 whitespace-nowrap text-xs sm:text-sm text-center text-gray-500">{losses}</td>
                                            <td className="py-1 px-1 sm:py-2 sm:px-6 whitespace-nowrap text-xs sm:text-sm text-center text-gray-500">{startPoints}</td>
                                            <td className="py-1 px-1 sm:py-2 sm:px-6 whitespace-nowrap text-xs sm:text-sm text-center text-gray-500">{endPoints}</td>
                                            <td className="py-1 px-1 sm:py-2 sm:px-6 whitespace-nowrap text-xs sm:text-sm text-center font-semibold">
                                                <span className={change >= 0 ? 'text-green-500' : 'text-red-500'}>
                                                    {change >= 0 ? `+${change}` : change}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="7" className="text-center">No results found</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Profile Overlay */}
            {selectedPlayer && (
                <ProfileOverlay 
                    player={getPlayerData(selectedPlayer)} 
                    onClose={() => setSelectedPlayer(null)} 
                />
            )}

            {/* Pool Selection Modal */}
            <PoolSelectionModal 
                isOpen={showPoolModal}
                onClose={() => setShowPoolModal(false)}
                pools={pools}
                onSelect={setPoolFilter}
            />
        </div>
    );
};

export default Results; 